import { useId } from 'react';
import type { DisplayPrice } from '../lib/pricing';
import { ceilingUsd, quotedRateLabel, quotedRateUsd } from '../lib/pricing';
import { usdCost } from '../lib/format';
import { cn } from '../lib/utils';
import { PriceExplainer } from './PriceExplainer';
import { m } from '../paraglide/messages';

export interface PriceTagProps {
  price: DisplayPrice;
  /** small caps label above the amount, e.g. `from` or `price` */
  label?: string;
  /** headline type scale; `sm` is the table/inline size */
  size?: 'sm' | 'md';
  /** render the hover/focus explainer on a metered price. Off where there is no
   *  room for a card (search palette rows, dense list cells). */
  explain?: boolean;
  /** trailing copy on the second line for a flat price, e.g. `10 endpoints` */
  flatNote?: string;
  className?: string;
}

const AMOUNT = { sm: 'text-14', md: 'text-18' } as const;

/**
 * The one price display. The headline is 1,000 of whatever the offer bills for,
 * so a flat price reads `/1k req` and a metered one reads `/1k results` - the
 * quantity the customer is actually buying, and small enough figures to compare
 * by eye. A metered price then carries its per-request ceiling on the line
 * below, because that is the number that is a ceiling; putting `up to` on a
 * per-1,000-results rate would qualify the wrong figure. The meter color and the
 * explainer card say the same thing two more ways.
 *
 * CSS-only hover/focus: the card is always in the markup, so the first client
 * render matches the server render on the prerendered marketing routes.
 */
export const PriceTag = ({
  price,
  label,
  size = 'md',
  explain = false,
  flatNote,
  className,
}: PriceTagProps) => {
  const tipId = useId();
  const showCard = explain && price.metered;
  return (
    <span
      className={cn(
        'group/price relative inline-flex flex-col items-end',
        showCard && 'cursor-help rounded-6 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent',
        className,
      )}
      {...(showCard ? { tabIndex: 0, 'aria-describedby': tipId } : {})}
    >
      {label ? (
        <span className="text-11 text-text-3">{label}</span>
      ) : null}
      <span
        className={cn(
          'mono font-medium tracking-[-0.01em] whitespace-nowrap',
          AMOUNT[size],
          price.metered
            ? 'border-b border-dotted border-meter-line pb-px text-meter group-hover/price:border-solid group-focus-visible/price:border-solid'
            : 'text-accent',
        )}
      >
        {usdCost(quotedRateUsd(price))}
        <span className="ml-1 text-10 font-medium text-text-3">{quotedRateLabel(price)}</span>
      </span>
      {price.metered ? (
        <span className="mono mt-px flex items-center gap-1 text-11 whitespace-nowrap text-text-3">
          <span className="size-1.5 shrink-0 rounded-full bg-meter" />
          {m.measure_price_ceiling({ amount: usdCost(ceilingUsd(price)) })}
        </span>
      ) : flatNote ? (
        <span className="mono mt-px text-11 whitespace-nowrap text-text-3">{flatNote}</span>
      ) : null}
      {showCard ? <PriceExplainer id={tipId} price={price} /> : null}
    </span>
  );
};
